import { useEffect, useState } from 'react';
import { Download, Share2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, apiError } from '@/lib/pos-api';
import { whatsappReceiptUrl } from '@/lib/draft-receipt.mjs';

export default function ReceiptDocumentShare({ tx, phone }) {
  const [number, setNumber] = useState(phone || '');
  const [document, setDocument] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [sharing, setSharing] = useState(false);
  // Capture the opened cart; changing the phone number must not rebuild its receipt.
  const [snapshot] = useState(tx);
  useEffect(() => {
    const controller = new AbortController();
    let url;
    setDocument(null); setError('');
    api.post('/print/draft/document', snapshot, { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      const bytes = Uint8Array.from(atob(data.pdf), char => char.charCodeAt(0));
      const file = new File([bytes], data.filename, { type: 'application/pdf' });
      url = URL.createObjectURL(file);
      setDocument({ ...data, file, url });
    }).catch(error => { if (!controller.signal.aborted) setError(apiError(error)); });
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [snapshot, attempt]);
  const canShare = !!document && !!navigator.share && !!navigator.canShare?.({ files: [document.file] });
  const chatUrl = whatsappReceiptUrl(number, 'Berikut resi pesanan Anda.');
  const share = async () => {
    setSharing(true); setError('');
    try {
      await navigator.share({ files: [document.file], title: 'Resi Pesanan' });
    } catch (error) {
      if (error.name !== 'AbortError') setError('Belum bisa membagikan file. Unduh PDF lalu lampirkan di WhatsApp.');
    } finally { setSharing(false); }
  };
  return <div className="space-y-3" data-testid="receipt-document-share">
    {!document && !error && <p role="status" className="text-sm text-ui-muted">Menyiapkan resi PDF…</p>}
    {error && <div role="alert" className="space-y-2 text-sm text-danger-readable"><p>{error}</p>{!document && <Button type="button" variant="outline" onClick={() => setAttempt(value => value + 1)}><RefreshCw className="h-4 w-4 mr-2" />Coba lagi</Button>}</div>}
    {document && <>
      <div data-testid="draft-receipt-preview" className="max-h-64 overflow-y-auto rounded-lg border border-ui-line bg-white p-2 space-y-2">
        {document.previews.map((png, index) => <img key={index} src={'data:image/png;base64,' + png} alt={`Resi halaman ${index + 1} dari ${document.pages}`} className="w-full h-auto" />)}
      </div>
      <p className="text-xs text-ui-muted">PDF · {document.pages} halaman · tampilan seperti cetak resi</p>
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline"><a data-testid="download-receipt-pdf" href={document.url} download={document.filename}><Download className="h-4 w-4 mr-1" />Unduh PDF</a></Button>
        {canShare && <Button type="button" data-testid="share-receipt-pdf" onClick={share} disabled={sharing} className="bg-brand-button hover:bg-brand-hover"><Share2 className="h-4 w-4 mr-1" />{sharing ? 'Membagikan…' : 'Bagikan PDF'}</Button>}
      </div>
      {canShare && <p className="text-xs text-ui-muted">Pilih WhatsApp, lalu pilih pelanggan pada menu berbagi.</p>}
      <div className="space-y-2 border-t border-ui-line pt-3">
        <label htmlFor="receipt-phone" className="text-sm">Nomor WhatsApp pelanggan</label>
        <Input id="receipt-phone" data-testid="receipt-phone-input" type="tel" value={number} onChange={event => setNumber(event.target.value)} placeholder="08… atau +62…" className="bg-ui-soft border-ui-line" />
        {number && !chatUrl && <p className="text-xs text-danger-readable">Masukkan nomor WhatsApp yang valid.</p>}
        <p className="text-xs text-ui-muted">Untuk WhatsApp Web: unduh PDF, buka chat pelanggan, lalu lampirkan PDF sebagai dokumen.</p>
        {chatUrl ? <Button asChild variant="outline" className="w-full"><a data-testid="open-receipt-whatsapp" href={chatUrl} target="_blank" rel="noopener noreferrer">Buka chat pelanggan</a></Button> : <Button type="button" variant="outline" disabled className="w-full">Buka chat pelanggan</Button>}
      </div>
    </>}
  </div>;
}
